/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { NgClass } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { MatChipListboxChange, MatChipsModule } from "@angular/material/chips";
import { ChipOption } from "../../models/ui/chip-option.model";
import { ButtonComponent } from "../button/button.component";

@Component({
	selector: "globalictcc-chips",
	standalone: true,
	imports: [MatChipsModule, ButtonComponent, NgClass],
	templateUrl: "./chips.component.html",
	styleUrl: "./chips.component.scss"
})
export class ChipsComponent implements OnChanges {
	@Input() public chips: ChipOption[] = [];
	@Input() public removable: boolean = false;

	@Output() public handleSelection = new EventEmitter<string | number>();
	@Output() public handleRemoval = new EventEmitter<ChipOption[]>();

	ngOnChanges(changes: SimpleChanges) {
		const chipsChanges = changes["chips"];
		if (!chipsChanges || chipsChanges.isFirstChange()) return;

		const previousChips = chipsChanges.previousValue as ChipOption[];
		const currentChips = chipsChanges.currentValue as ChipOption[];
		const selectedChip = currentChips.find(chip => chip.selected);
		const selectedChipIndex = selectedChip ? currentChips.indexOf(selectedChip) : 0;

		const chipRemoved = previousChips.length > currentChips.length;
		const changedIndex = chipRemoved
			? this._findRemovedChipIndex(previousChips, currentChips)
			: selectedChipIndex;

		this._updateSelection(changedIndex);
	}

	/**
	 * Finds the index of the chip that was removed from the previous list of chips.
	 *
	 * @param previousChips - The array of chips before the change.
	 * @param currentChips - The array of chips after the change.
	 * @returns The index of the removed chip in the previousChips array, or -1 if no chip was removed.
	 */
	private _findRemovedChipIndex(previousChips: ChipOption[], currentChips: ChipOption[]): number {
		return previousChips.findIndex(chip => !currentChips.includes(chip));
	}

	/**
	 * Handles the selection change event for the chip list.
	 *
	 * @param event - The selection change event from the MatChipListbox.
	 *                Contains the value of the selected chip.
	 *
	 * If the event value is null or undefined, the function returns early.
	 * Finds the index of the selected chip in the chips array by matching the chip label with the event value.
	 * If the chip is not found (index is -1), the function returns early.
	 * Otherwise, it updates the selection using the found chip index.
	 */
	public onSelectionChange(event: MatChipListboxChange) {
		if (!event.value) return;

		const chipIndex = this.chips.findIndex(chip => chip.label === event.value);
		if (chipIndex === -1) return;

		this._updateSelection(chipIndex);
	}

	/**
	 * Removes a chip from the list at the specified index.
	 *
	 * @param {number} index - The index of the chip to be removed.
	 *
	 * @remarks
	 * This method will not remove a chip if the `removable` property is set to false,
	 * or if the index is out of bounds.
	 *
	 * @fires handleRemoval - Emits the updated list of chips after removal.
	 */
	public onRemoveChip(index: number) {
		if (!this.removable || index < 0 || index >= this.chips.length) return;

		this.chips.splice(index, 1);
		this._updateSelection(index);
		this.handleRemoval.emit(this.chips);
	}

	/**
	 * Updates the selection of chips based on the provided index.
	 * If the chips array is empty, it emits `undefined` through the `handleSelection` event.
	 * Otherwise, it selects the chip at the given index or the last chip if the index is out of bounds.
	 * It then emits the value of the newly selected chip through the `handleSelection` event.
	 *
	 * @private
	 * @param {number} index - The index of the chip to select.
	 */
	private _updateSelection(index: number) {
		if (this.chips.length === 0) {
			this.handleSelection.emit(undefined);
			return;
		}

		const newSelectedIndex = Math.min(index, this.chips.length - 1);
		this.chips = this.chips.map((chip, idx) => ({
			...chip,
			selected: idx === newSelectedIndex
		}));

		this.handleSelection.emit(this.chips[newSelectedIndex].value);
	}
}
